import { create } from "zustand";

export type ToastType = "success" | "error" | "info" | "warning";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastState {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

// CRITICAL: Track recent admin error toasts to prevent spam
// Store toast message + timestamp to dedupe within 10 seconds
const recentAdminErrorToasts = new Map<string, number>();
const ADMIN_ERROR_DEDUPE_WINDOW_MS = 10000; // 10 seconds

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  addToast: (toast) => {
    // Ensure message is a string
    const message = typeof toast.message === "string" ? toast.message : String(toast.message || "");
    
    // CRITICAL: Deduplicate admin access denied toasts
    // Prevent multiple "Admin Access Denied" toasts from stacking
    const isAdminError = 
      message?.toLowerCase().includes("admin access") ||
      message?.toLowerCase().includes("admin required") ||
      message?.toLowerCase().includes("admin denied");
    
    if (isAdminError && toast.type === "error") {
      const now = Date.now();
      const messageKey = message.toLowerCase().trim();
      const lastShown = recentAdminErrorToasts.get(messageKey);
      
      // If same admin error was shown within dedupe window, skip it
      if (lastShown && (now - lastShown) < ADMIN_ERROR_DEDUPE_WINDOW_MS) {
        console.log("[Toast Store] Deduplicating admin error toast:", message);
        // Return a fake ID (toast won't be shown)
        return `dedupe-${Date.now()}`;
      }
      
      // Track this admin error
      recentAdminErrorToasts.set(messageKey, now);
      
      // Clean up old entries (older than dedupe window)
      for (const [key, timestamp] of recentAdminErrorToasts.entries()) {
        if (now - timestamp > ADMIN_ERROR_DEDUPE_WINDOW_MS) {
          recentAdminErrorToasts.delete(key);
        }
      }
    }
    
    const id = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `toast-${Date.now()}-${Math.random()}`;
    
    const newToast: Toast = {
      ...toast,
      message,
      id,
      duration: toast.duration ?? 5000,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-dismiss after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, newToast.duration);
    }

    return id;
  },
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
  clearToasts: () => set({ toasts: [] }),
}));
